/* eslint-disable */
// Prospects-1.jsx
// Scraped + skip-traced records pre-outreach.
// Drop into ~/wholesaleiq/src/Prospects-1.jsx (rename to Prospects.jsx on deploy).
//
// Props:
//   currentUser   - the auth user (with .owner_id resolved by App.jsx for team members)
//   subscription  - subscription state from App.jsx ({tier, status, ...})
//   onGoToCRM     - optional: navigate to pipeline view

import { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "./supabaseClient";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
  || "https://wholesaleiq-backend-production-aedc.up.railway.app";

const PAGE_SIZE = 50;
const SOURCES = ["treasurer", "recorder", "sheriff", "city"];
const STAGES = ["New", "Skip Trace Pending", "Skip Traced", "Cold", "In Outreach", "Promoted", "DNC", "Excluded", "Archived"];

// Stage → accent color
const stageColor = (stage) => ({
  "New":                 "#7E9BD4",
  "Skip Trace Pending":  "#C9935A",
  "Skip Traced":         "#5A8A60",
  "Cold":                "#6B6B70",
  "In Outreach":         "#C9A96E",
  "Promoted":            "#5A8A60",
  "DNC":                 "#C06149",
  "Excluded":            "#45454A",
  "Archived":            "#45454A",
}[stage] || "#6B6B70");

export default function Prospects({ currentUser, subscription, onGoToCRM }) {
  const ownerId = currentUser?.owner_id || currentUser?.id;
  const [prospects, setProspects]  = useState([]);
  const [loading, setLoading]      = useState(true);
  const [error, setError]          = useState(null);
  const [selected, setSelected]    = useState(new Set());
  const [filters, setFilters]      = useState({ source: "all", stage: "all", minScore: 0, search: "" });
  const [page, setPage]            = useState(0);
  const [total, setTotal]          = useState(0);
  const [busyId, setBusyId]        = useState(null);
  const [bulkBusy, setBulkBusy]    = useState(false);
  const [scrapeBusy, setScrapeBusy] = useState(false);
  const [statsBar, setStatsBar]    = useState(null);

  const isPro = ["pro", "team"].includes(subscription?.tier)
    && ["active", "trialing"].includes(subscription?.status);

  // ---- Data fetch ---------------------------------------------------------
  const fetchProspects = useCallback(async () => {
    if (!ownerId) return;
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from("prospects")
        .select("*", { count: "exact" })
        .eq("user_id", ownerId)
        .eq("owner_type", "person")    // persons-only filter (entities/trusts/estates excluded)
        .order("motivation_score", { ascending: false })
        .order("created_at", { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (filters.source !== "all") query = query.eq("source", filters.source);
      if (filters.stage !== "all")  query = query.eq("stage", filters.stage);
      if (filters.minScore > 0)     query = query.gte("motivation_score", filters.minScore);
      if (filters.search) {
        query = query.or(`owner_name_raw.ilike.%${filters.search}%,parcel_id.ilike.%${filters.search}%`);
      }

      const { data, error: err, count } = await query;
      if (err) throw err;
      setProspects(data || []);
      setTotal(count || 0);
    } catch (err) {
      console.error("[prospects] fetch failed", err);
      setError(err.message || "Failed to load prospects");
    } finally {
      setLoading(false);
    }
  }, [ownerId, filters, page]);

  useEffect(() => { fetchProspects(); }, [fetchProspects]);

  // ---- Stats summary ------------------------------------------------------
  useEffect(() => {
    if (!ownerId) return;
    (async () => {
      const { data } = await supabase
        .from("prospects")
        .select("stage, motivation_score")
        .eq("user_id", ownerId)
        .eq("owner_type", "person");
      if (!data) return;
      const stats = {
        total: data.length,
        new: data.filter(p => p.stage === "New").length,
        traced: data.filter(p => p.stage === "Skip Traced" || p.stage === "In Outreach").length,
        hot: data.filter(p => (p.motivation_score || 0) >= 50).length,
      };
      setStatsBar(stats);
    })();
  }, [ownerId, prospects.length]);

  // ---- Backend calls ------------------------------------------------------
  const authHeaders = async () => {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const runSkipTrace = async (prospectId) => {
    if (!isPro) { alert("Skip trace is a Pro/Team feature."); return; }
    setBusyId(prospectId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospects/${prospectId}/skip-trace`, {
        method: "POST",
        headers: await authHeaders(),
      });
      if (!res.ok) throw new Error(await res.text() || `Skip trace failed (${res.status})`);
      await fetchProspects();
    } catch (err) {
      alert(`Skip trace failed: ${err.message}`);
    } finally { setBusyId(null); }
  };

  const runBulkSkipTrace = async () => {
    if (!isPro) { alert("Skip trace is a Pro/Team feature."); return; }
    if (selected.size === 0) return;
    if (!window.confirm(`Skip trace ${selected.size} prospects? Each costs ~$0.10 in BatchData credits.`)) return;
    setBulkBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospects/skip-trace-bulk`, {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify({ ids: Array.from(selected) }),
      });
      if (!res.ok) throw new Error(await res.text());
      setSelected(new Set());
      // Bulk runs async on the server — poll/refresh after a delay
      setTimeout(() => fetchProspects(), 2000);
    } catch (err) {
      alert(`Bulk skip trace failed: ${err.message}`);
    } finally { setBulkBusy(false); }
  };

  const runTreasurerScrape = async () => {
    if (!isPro) { alert("Scraping is a Pro/Team feature."); return; }
    if (!window.confirm("Run Treasurer scrape for Lake County (Gary)? This populates your Prospects table with delinquency data.")) return;
    setScrapeBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/scrape/treasurer`, {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify({ market: "gary", parcelPrefixes: ["45-08", "45-09"] }),
      });
      if (!res.ok) throw new Error(await res.text());
      alert("Scrape started. Records will appear here as they're processed (refresh in a minute).");
    } catch (err) {
      alert(`Scrape start failed: ${err.message}`);
    } finally { setScrapeBusy(false); }
  };

  const archiveProspect = async (prospectId) => {
    setBusyId(prospectId);
    try {
      const { error: err } = await supabase.from("prospects").update({ stage: "Archived" }).eq("id", prospectId);
      if (err) throw err;
      await fetchProspects();
    } catch (err) {
      alert(`Archive failed: ${err.message}`);
    } finally { setBusyId(null); }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleSelectAll = () => {
    if (selected.size === prospects.length) setSelected(new Set());
    else setSelected(new Set(prospects.map(p => p.id)));
  };

  // ---- Helpers ------------------------------------------------------------
  const formatAddress = (addr) => {
    if (!addr || typeof addr !== "object") return "—";
    return [addr.line1, addr.city, addr.state, addr.zip].filter(Boolean).join(", ");
  };
  const phoneCount = (contacts) => (Array.isArray(contacts) ? contacts.filter(c => c.type === "phone").length : 0);
  const cleanMobileCount = (contacts) => (Array.isArray(contacts)
    ? contacts.filter(c => c.type === "phone" && c.line_type === "mobile" && c.dnc === false && c.litigator === false).length
    : 0);

  // ─── UI ───────────────────────────────────────────────────────────────────
  return (
    <div style={shell}>
      {/* Header */}
      <div style={headerRow}>
        <div>
          <div style={kicker}>WHOLESALEIQ / PROSPECTS</div>
          <h1 style={h1}>Prospects</h1>
          <div style={subline}>
            Scraped + classified records, persons only.
            <span style={{ color: "#45454A" }}> LLCs, trusts, and estates excluded.</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={fetchProspects} style={btnGhost}>REFRESH</button>
          <button onClick={runTreasurerScrape} disabled={scrapeBusy || !isPro}
            style={{ ...btnPrimary, opacity: (scrapeBusy || !isPro) ? 0.5 : 1, cursor: (scrapeBusy || !isPro) ? "not-allowed" : "pointer" }}
            title={!isPro ? "Pro/Team only" : ""}>
            {scrapeBusy ? "STARTING…" : "+ RUN SCRAPE"}
          </button>
        </div>
      </div>

      {/* Stats bar */}
      {statsBar && (
        <div style={statsRow}>
          <Stat label="TOTAL" value={statsBar.total} />
          <Stat label="NEW" value={statsBar.new} accent="#7E9BD4" />
          <Stat label="SKIP TRACED" value={statsBar.traced} accent="#5A8A60" />
          <Stat label="HOT (50+)" value={statsBar.hot} accent="#C9A96E" />
        </div>
      )}

      {/* Filters */}
      <div style={filterRow}>
        <select value={filters.source} onChange={e => { setFilters({...filters, source: e.target.value}); setPage(0); }} style={inp}>
          <option value="all">All sources</option>
          {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.stage} onChange={e => { setFilters({...filters, stage: e.target.value}); setPage(0); }} style={inp}>
          <option value="all">All stages</option>
          {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input type="number" placeholder="Min score" value={filters.minScore || ""}
          onChange={e => { setFilters({...filters, minScore: parseInt(e.target.value) || 0}); setPage(0); }}
          style={{ ...inp, width: 110 }} />
        <input type="text" placeholder="Search name or parcel..." value={filters.search}
          onChange={e => { setFilters({...filters, search: e.target.value}); setPage(0); }}
          style={{ ...inp, flex: 1, minWidth: 240 }} />
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div style={bulkBar}>
          <span style={{ color: "#C9A96E", fontFamily: "'DM Mono',monospace", fontSize: 12, letterSpacing: 1 }}>
            {selected.size} SELECTED
          </span>
          <button onClick={runBulkSkipTrace} disabled={bulkBusy || !isPro}
            style={{ ...btnPrimary, opacity: (bulkBusy || !isPro) ? 0.5 : 1, cursor: (bulkBusy || !isPro) ? "not-allowed" : "pointer" }}>
            {bulkBusy ? "WORKING…" : "SKIP TRACE SELECTED"}
          </button>
          <button onClick={() => setSelected(new Set())} style={btnGhost}>CLEAR</button>
        </div>
      )}

      {!isPro && (
        <div style={infoBanner}>
          ⚡ Skip tracing and bulk actions require a Pro or Team plan. You can still view scraped data on Starter.
        </div>
      )}

      {error && (
        <div style={errorBanner}>⚠ {error}</div>
      )}

      {/* Table */}
      <div style={tableWrap}>
        {loading && prospects.length === 0 ? (
          <div style={emptyState}>
            <div style={{ width: 32, height: 32, border: "2px solid #1C1C1F", borderTopColor: "#C9A96E", borderRadius: "50%", animation: "spin .7s linear infinite", margin: "0 auto 16px" }} />
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, letterSpacing: 2, color: "#45454A" }}>LOADING PROSPECTS...</div>
            <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
          </div>
        ) : prospects.length === 0 ? (
          <div style={emptyState}>
            <div style={{ fontSize: 48, opacity: 0.3 }}>🏚</div>
            <div style={{ marginTop: 12, color: "#E8E6E1", fontSize: 16, fontWeight: 600 }}>No prospects yet</div>
            <div style={{ marginTop: 6, color: "#6B6B70", fontSize: 13 }}>
              Run a scrape to populate this table with delinquent property owners.
            </div>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#0A0A0B", borderBottom: "1px solid #1C1C1F" }}>
                <th style={{...th, width: 40}}>
                  <input type="checkbox"
                    checked={prospects.length > 0 && selected.size === prospects.length}
                    onChange={toggleSelectAll} />
                </th>
                <th style={th}>PROPERTY</th>
                <th style={th}>OWNER</th>
                <th style={th}>SCORE</th>
                <th style={th}>SOURCE</th>
                <th style={th}>STAGE</th>
                <th style={th}>CONTACTS</th>
                <th style={th}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {prospects.map(p => (
                <tr key={p.id} style={{
                  borderBottom: "1px solid #1C1C1F",
                  background: selected.has(p.id) ? "#C9A96E08" : "transparent",
                }}>
                  <td style={td}>
                    <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)} />
                  </td>
                  <td style={td}>
                    <div style={{ color: "#E8E6E1", fontSize: 13 }}>{formatAddress(p.property_address)}</div>
                    {p.parcel_id && (
                      <div style={{ color: "#45454A", fontSize: 11, fontFamily: "'DM Mono',monospace", marginTop: 2 }}>
                        {p.parcel_id}
                      </div>
                    )}
                  </td>
                  <td style={td}>
                    <div style={{ color: "#E8E6E1", fontSize: 13 }}>{p.owner_name_raw}</div>
                    {p.is_absentee && (
                      <span style={{
                        display: "inline-block", marginTop: 3, padding: "1px 7px", borderRadius: 3,
                        background: "#C9935A18", color: "#C9935A", fontSize: 10,
                        fontFamily: "'DM Mono',monospace", letterSpacing: 1,
                      }}>ABSENTEE</span>
                    )}
                  </td>
                  <td style={td}>
                    <span style={{
                      color: (p.motivation_score || 0) >= 50 ? "#C9A96E" : "#E8E6E1",
                      fontWeight: (p.motivation_score || 0) >= 50 ? 700 : 500,
                      fontFamily: "'DM Mono',monospace",
                      fontSize: 13,
                    }}>{p.motivation_score || 0}</span>
                  </td>
                  <td style={{ ...td, color: "#6B6B70", textTransform: "capitalize", fontSize: 12 }}>
                    {p.source}
                  </td>
                  <td style={td}>
                    <span style={{
                      padding: "3px 9px", borderRadius: 3,
                      fontSize: 10, fontFamily: "'DM Mono',monospace", letterSpacing: 1,
                      background: stageColor(p.stage) + "18",
                      color: stageColor(p.stage),
                      border: `1px solid ${stageColor(p.stage)}33`,
                    }}>{p.stage.toUpperCase()}</span>
                  </td>
                  <td style={td}>
                    {p.skip_trace_status === "completed" ? (
                      <span style={{ fontSize: 12, fontFamily: "'DM Mono',monospace" }}>
                        <span style={{ color: "#5A8A60" }}>{cleanMobileCount(p.contacts)}</span>
                        <span style={{ color: "#45454A" }}> / {phoneCount(p.contacts)}</span>
                      </span>
                    ) : (
                      <span style={{ color: "#45454A", fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td style={td}>
                    <div style={{ display: "flex", gap: 6 }}>
                      {p.skip_trace_status !== "completed" && (
                        <button onClick={() => runSkipTrace(p.id)}
                          disabled={busyId === p.id || !isPro}
                          style={{ ...btnSmall, opacity: (busyId === p.id || !isPro) ? 0.5 : 1 }}>
                          {busyId === p.id ? "…" : "TRACE"}
                        </button>
                      )}
                      {p.skip_trace_status === "completed" && cleanMobileCount(p.contacts) > 0 && (
                        <button onClick={() => alert("Add to Outreach campaign — wire up next")} style={btnSmall}>
                          OUTREACH
                        </button>
                      )}
                      <button onClick={() => archiveProspect(p.id)} disabled={busyId === p.id} style={btnSmallGhost}>
                        ARCHIVE
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div style={paginationRow}>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: "#6B6B70", letterSpacing: 1 }}>
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} OF {total.toLocaleString()}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} style={btnGhost}>← PREV</button>
            <button onClick={() => setPage(page + 1)} disabled={(page + 1) * PAGE_SIZE >= total} style={btnGhost}>NEXT →</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Stat tile ──────────────────────────────────────────────────────────────
function Stat({ label, value, accent }) {
  return (
    <div style={{
      flex: 1, background: "#111113", border: "1px solid #1C1C1F", borderRadius: 8,
      padding: "14px 18px", fontFamily: "'DM Sans',sans-serif",
    }}>
      <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, letterSpacing: 2, color: "#45454A" }}>
        {label}
      </div>
      <div style={{
        fontSize: 26, fontWeight: 800, color: accent || "#E8E6E1", marginTop: 4,
      }}>{value}</div>
    </div>
  );
}

// ─── Inline style objects ───────────────────────────────────────────────────
const shell = {
  background: "#0A0A0B", color: "#E8E6E1", flex: 1, overflow: "auto",
  padding: "24px 32px", fontFamily: "'DM Sans',sans-serif",
};

const headerRow = {
  display: "flex", alignItems: "flex-start", justifyContent: "space-between",
  marginBottom: 24, gap: 16, flexWrap: "wrap",
};

const kicker = {
  fontFamily: "'DM Mono',monospace", fontSize: 10, letterSpacing: 3, color: "#45454A", marginBottom: 6,
};

const h1 = { margin: 0, fontSize: 28, fontWeight: 800, color: "#E8E6E1", letterSpacing: -0.5 };

const subline = { color: "#6B6B70", fontSize: 13, marginTop: 6 };

const statsRow = { display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" };

const filterRow = {
  display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center",
};

const inp = {
  background: "#111113", color: "#E8E6E1", border: "1px solid #1C1C1F",
  borderRadius: 6, padding: "8px 12px", fontSize: 13, outline: "none",
  fontFamily: "'DM Sans',sans-serif",
};

const bulkBar = {
  background: "#C9A96E0E", border: "1px solid #C9A96E55", borderRadius: 8,
  padding: "10px 14px", marginBottom: 12, display: "flex", alignItems: "center", gap: 12,
};

const infoBanner = {
  background: "#C9A96E0A", border: "1px solid #C9A96E33", borderRadius: 8,
  padding: "10px 14px", marginBottom: 12, color: "#C9A96E", fontSize: 13,
};

const errorBanner = {
  background: "#C0614911", border: "1px solid #C0614933", borderRadius: 8,
  padding: "10px 14px", marginBottom: 12, color: "#C06149", fontSize: 13,
};

const tableWrap = {
  background: "#111113", border: "1px solid #1C1C1F", borderRadius: 8, overflow: "auto",
};

const emptyState = {
  padding: "64px 24px", textAlign: "center",
};

const th = {
  textAlign: "left", padding: "12px 14px",
  color: "#45454A", fontFamily: "'DM Mono',monospace",
  fontSize: 10, fontWeight: 700, letterSpacing: 2,
};

const td = {
  padding: "12px 14px", verticalAlign: "top",
  fontFamily: "'DM Sans',sans-serif",
};

const btnPrimary = {
  background: "linear-gradient(135deg,#C9A96E,#A8834A)", color: "#0F0F11",
  border: "none", borderRadius: 6, padding: "8px 16px",
  fontSize: 11, fontWeight: 800, letterSpacing: 1.5,
  cursor: "pointer", fontFamily: "'DM Mono',monospace",
};

const btnGhost = {
  background: "transparent", color: "#6B6B70",
  border: "1px solid #2A2A2E", borderRadius: 6, padding: "8px 16px",
  fontSize: 11, fontWeight: 700, letterSpacing: 1.5,
  cursor: "pointer", fontFamily: "'DM Mono',monospace",
};

const btnSmall = {
  background: "linear-gradient(135deg,#C9A96E,#A8834A)", color: "#0F0F11",
  border: "none", borderRadius: 4, padding: "4px 9px",
  fontSize: 10, fontWeight: 800, letterSpacing: 1,
  cursor: "pointer", fontFamily: "'DM Mono',monospace",
};

const btnSmallGhost = {
  background: "transparent", color: "#6B6B70",
  border: "1px solid #2A2A2E", borderRadius: 4, padding: "4px 9px",
  fontSize: 10, fontWeight: 700, letterSpacing: 1,
  cursor: "pointer", fontFamily: "'DM Mono',monospace",
};

const paginationRow = {
  display: "flex", justifyContent: "space-between", alignItems: "center",
  marginTop: 16, padding: "0 4px",
};
